import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Sidebar } from '../components/Sidebar';
import { api } from '../services/api';
import { Trash2, Plus, Edit2, Save } from 'lucide-react';
import { ConfigResponse } from '../types';

interface SettingsPageProps {
  currentPage: string;
  activeAlertsCount: number;
  tenantId: string;
  onNavigate: (page: string) => void;
  onLogout: () => void;
}

// Constants
const ACTIVE_TAB_DATA_SOURCES = 'data-sources';
const ACTIVE_TAB_TEAMS = 'teams';
const ACTIVE_TAB_SERVICES = 'services';
const DATA_SOURCE_GRAFANA = 'Grafana';
const DATA_SOURCE_GITHUB = 'GitHub';
const DATA_SOURCE_NOTION = 'Notion';
const DATA_SOURCE_GRAFANA_ONCALL = 'Grafana OnCall';

// Data sources configuration - all are viewable but read-only
const allDataSources = [
  { name: DATA_SOURCE_GRAFANA, logo: 'grafana.svg', configurable: true },
  { name: DATA_SOURCE_GRAFANA_ONCALL, logo: 'grafana.svg', configurable: true },
  { name: DATA_SOURCE_GITHUB, logo: 'github.svg', configurable: true },
  { name: DATA_SOURCE_NOTION, logo: 'notion.svg', configurable: true }
];

// Local interface for form data (converted from API response)
interface ServiceFormData {
  github: {
    repo: string;
    default_branch: string;
  };
  grafana: {
    dashboard_uid: string;
    dashboard_name: string;
    variables?: {
      namespace?: string;
      datasource?: string;
    };
    prometheus_datasource_uid?: string;
    loki_datasource_uid?: string;
  };
  team_id: string;
  description: string;
}



export const SettingsPage: React.FC<SettingsPageProps> = ({
  currentPage,
  activeAlertsCount,
  tenantId,
  onNavigate,
  onLogout,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = useState<'data-sources' | 'teams' | 'services'>(ACTIVE_TAB_DATA_SOURCES);
  const [configData, setConfigData] = useState<ConfigResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [editingServiceName, setEditingServiceName] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<{id: string, name: string} | null>(null);
  const [showAddService, setShowAddService] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [editFormData, setEditFormData] = useState<ServiceFormData | null>(null);
  const [originalEditFormData, setOriginalEditFormData] = useState<ServiceFormData | null>(null);
  const [tenantConfigFormData, setTenantConfigFormData] = useState<any>(null);
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null);

  // Team editing state
  const [editingTeam, setEditingTeam] = useState<string | null>(null);
  const [editingTeamName, setEditingTeamName] = useState<string>('');
  const [editingTeamSlackChannel, setEditingTeamSlackChannel] = useState<string>('');

  // Team deletion state
  const [showTeamDeleteConfirm, setShowTeamDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<{id: string, name: string} | null>(null);

  // Team add state
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamSlackChannel, setNewTeamSlackChannel] = useState('');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const config = await api.getConfig(token);
      setConfigData(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (activeTab === ACTIVE_TAB_SERVICES || activeTab === ACTIVE_TAB_DATA_SOURCES || activeTab === ACTIVE_TAB_TEAMS) {
      loadConfig();
    }
  }, [activeTab, loadConfig]);

  const handleEditService = (serviceId: string, serviceName: string, config: ServiceFormData) => {
    setEditingService(serviceId);
    setEditingServiceName(serviceName);
    const configCopy = JSON.parse(JSON.stringify(config));
    setEditFormData(configCopy);
    setOriginalEditFormData(configCopy); // Deep clone
  };

  const handleSaveService = async () => {
    if (!editingService || !editFormData) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.updateService(token, editingService, editFormData);
      setSuccessMessage(`Service updated successfully!`);
      setEditingService(null);
      setEditingServiceName(null);
      setEditFormData(null);
      await loadConfig();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!newServiceName || !editFormData) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.createService(token, newServiceName, editFormData);
      setSuccessMessage(`Service '${newServiceName}' created successfully!`);
      setShowAddService(false);
      setNewServiceName('');
      setEditFormData(null);
      await loadConfig();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = (serviceId: string, serviceName: string) => {
    setServiceToDelete({ id: serviceId, name: serviceName });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.deleteService(token, serviceToDelete.id);
      setSuccessMessage(`Service '${serviceToDelete.name}' deleted successfully!`);
      await loadConfig();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete service');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setServiceToDelete(null);
    }
  };

  const cancelDeleteService = () => {
    setShowDeleteConfirm(false);
    setServiceToDelete(null);
  };

  const handleCreateTeam = async (teamName: string, slackChannel?: string) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.createTeam(token, teamName, slackChannel);
      setSuccessMessage(`Team '${teamName}' created successfully!`);
      setShowAddTeam(false);
      setNewTeamName('');
      setNewTeamSlackChannel('');
      await loadConfig();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeam = async () => {
    if (!newTeamName.trim()) {
      setError('Team name is required');
      return;
    }
    await handleCreateTeam(newTeamName.trim(), newTeamSlackChannel.trim() || undefined);
  };

  const handleCancelAddTeam = () => {
    setShowAddTeam(false);
    setNewTeamName('');
    setNewTeamSlackChannel('');
  };

  const handleEditTeam = (teamId: string, teamName: string, slackChannel: string) => {
    setEditingTeam(teamId);
    setEditingTeamName(teamName);
    setEditingTeamSlackChannel(slackChannel || '');
  };

  const handleSaveTeam = async () => {
    if (!editingTeam) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.updateTeam(token, editingTeam, editingTeamName, editingTeamSlackChannel);
      setSuccessMessage(`Team '${editingTeamName}' updated successfully!`);
      setEditingTeam(null);
      setEditingTeamName('');
      setEditingTeamSlackChannel('');
      await loadConfig();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update team');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditTeam = () => {
    setEditingTeam(null);
    setEditingTeamName('');
    setEditingTeamSlackChannel('');
  };

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    const servicesInTeam = configData?.service_configs.filter(s => s.team_id === teamId) || [];

    if (servicesInTeam.length > 0) {
      setError(`Cannot delete team '${teamName}' because it has ${servicesInTeam.length} service(s) assigned to it. Please reassign the services first.`);
      return;
    }

    setTeamToDelete({ id: teamId, name: teamName });
    setShowTeamDeleteConfirm(true);
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.deleteTeam(token, teamToDelete.id);
      setSuccessMessage(`Team '${teamToDelete.name}' deleted successfully!`);
      await loadConfig();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete team');
    } finally {
      setLoading(false);
      setShowTeamDeleteConfirm(false);
      setTeamToDelete(null);
    }
  };

  const cancelDeleteTeam = () => {
    setShowTeamDeleteConfirm(false);
    setTeamToDelete(null);
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setEditingServiceName(null);
    setEditFormData(null);
    setOriginalEditFormData(null);
    setShowAddService(false);
    setNewServiceName('');
    setTenantConfigFormData(null);
    setSelectedDataSource(null);

    // Reset team editing state
    setEditingTeam(null);
    setEditingTeamName('');
    setEditingTeamSlackChannel('');

    // Reset team deletion state
    setShowTeamDeleteConfirm(false);
    setTeamToDelete(null);

    // Reset team add state
    setShowAddTeam(false);
    setNewTeamName('');
    setNewTeamSlackChannel('');
  };

  const handleDataSourceClick = (sourceName: string) => {
    const dataSource = allDataSources.find(source => source.name === sourceName);
    if (dataSource?.configurable) {
      setSelectedDataSource(sourceName);
      if (configData?.tenant_config) {
        const tenantConfigCopy = JSON.parse(JSON.stringify(configData.tenant_config));
        setTenantConfigFormData(tenantConfigCopy);
      }
    }
  };

  // Check if there are unsaved service changes
  const hasUnsavedServiceChanges = () => {
    if (!editFormData || !originalEditFormData) return false;
    return JSON.stringify(editFormData) !== JSON.stringify(originalEditFormData);
  };

  const handleTenantConfigChange = (path: string[], value: string | number | boolean) => {
    if (!tenantConfigFormData) return;

    const newFormData = JSON.parse(JSON.stringify(tenantConfigFormData));
    let current: any = newFormData;

    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }

    current[path[path.length - 1]] = value;
    setTenantConfigFormData(newFormData);
  };

  const handleFormChange = (path: string[], value: string) => {
    if (!editFormData) return;

    const newFormData = JSON.parse(JSON.stringify(editFormData));
    let current: any = newFormData;

    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }

    current[path[path.length - 1]] = value;
    setEditFormData(newFormData);
  };

  const renderServiceForm = (serviceName: string, config: ServiceFormData) => {
    return (
      <div className="bg-[#2A2A2A] rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleCancelEdit}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h3 className="text-xl font-semibold text-white">
                {showAddService ? 'Add New Service' : `Edit ${serviceName}`}
              </h3>
              {!showAddService && (
                <p className="text-sm text-gray-400 mt-1">{config.description}</p>
              )}
            </div>
          </div>
          <button
            onClick={showAddService ? handleAddService : handleSaveService}
            disabled={loading || (showAddService ? !newServiceName : !hasUnsavedServiceChanges())}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{showAddService ? 'Create' : 'Save'}</span>
          </button>
        </div>

        {showAddService && (
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Service Name
              </label>
              <input
                type="text"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="e.g., payment-service"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={config.description}
                onChange={(e) => handleFormChange(['description'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="Service description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Team
              </label>
              <select
                value={config.team_id}
                onChange={(e) => handleFormChange(['team_id'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select a team</option>
                {configData?.team_configs.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.team_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {!showAddService && (
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={config.description}
                onChange={(e) => handleFormChange(['description'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="Service description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Team
              </label>
              <select
                value={config.team_id}
                onChange={(e) => handleFormChange(['team_id'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select a team</option>
                {configData?.team_configs.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.team_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* GitHub Configuration */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">GitHub</h4>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Repository
              </label>
              <input
                type="text"
                value={config.github.repo}
                onChange={(e) => handleFormChange(['github', 'repo'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="owner/repo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Default Branch
              </label>
              <input
                type="text"
                value={config.github.default_branch}
                onChange={(e) => handleFormChange(['github', 'default_branch'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="main"
              />
            </div>
          </div>

          {/* Grafana Configuration */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Grafana</h4>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Dashboard Name
              </label>
              <input
                type="text"
                value={config.grafana.dashboard_name}
                onChange={(e) => handleFormChange(['grafana', 'dashboard_name'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="app-metrics"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Dashboard UID
              </label>
              <input
                type="text"
                value={config.grafana.dashboard_uid}
                onChange={(e) => handleFormChange(['grafana', 'dashboard_uid'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="abc123xyz"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Prometheus Datasource UID
              </label>
              <input
                type="text"
                value={config.grafana.prometheus_datasource_uid || ''}
                onChange={(e) => handleFormChange(['grafana', 'prometheus_datasource_uid'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="WzwwB_S4z"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loki Datasource UID
              </label>
              <input
                type="text"
                value={config.grafana.loki_datasource_uid || ''}
                onChange={(e) => handleFormChange(['grafana', 'loki_datasource_uid'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="N5sgv_qnk"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Namespace
              </label>
              <input
                type="text"
                value={config.grafana.variables?.namespace || ''}
                onChange={(e) => handleFormChange(['grafana', 'variables', 'namespace'], e.target.value)}
                className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
                placeholder="default"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddTeamForm = () => {
    return (
      <div className="bg-[#2A2A2A] rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleCancelAddTeam}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h3 className="text-xl font-semibold text-white">Add New Team</h3>
            </div>
          </div>
          <button
            onClick={handleAddTeam}
            disabled={loading || !newTeamName.trim()}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>Create</span>
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Team Name
            </label>
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
              placeholder="e.g., engineering"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Slack Channel
            </label>
            <input
              type="text"
              value={newTeamSlackChannel}
              onChange={(e) => setNewTeamSlackChannel(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
              placeholder="#engineering"
            />
            <p className="text-xs text-gray-400 mt-1">
              Enter the alerts Slack channel name (e.g., #alerts)
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamForm = (teamName: string, slackChannel: string) => {
    return (
      <div className="bg-[#2A2A2A] rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleCancelEditTeam}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h3 className="text-xl font-semibold text-white">Edit Team: {teamName}</h3>
            </div>
          </div>
          <button
            onClick={handleSaveTeam}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>Save</span>
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Team Name
            </label>
            <input
              type="text"
              value={editingTeamName}
              onChange={(e) => setEditingTeamName(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
              placeholder="e.g., engineering"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Slack Channel
            </label>
            <input
              type="text"
              value={editingTeamSlackChannel}
              onChange={(e) => setEditingTeamSlackChannel(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white"
              placeholder="#engineering"
            />
            <p className="text-xs text-gray-400 mt-1">
              Enter the alerts Slack channel name (e.g., #alerts)
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-[#1A1A1A] text-white flex overflow-hidden">
      <Sidebar
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="text-[#8b949e]">Manage services and data sources</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-600">
          <button
            onClick={() => setActiveTab(ACTIVE_TAB_DATA_SOURCES)}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === ACTIVE_TAB_DATA_SOURCES
                ? 'border-green-500 text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Data Sources
          </button>
          <button
            onClick={() => setActiveTab(ACTIVE_TAB_TEAMS)}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === ACTIVE_TAB_TEAMS
                ? 'border-green-500 text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Teams
          </button>
          <button
            onClick={() => setActiveTab(ACTIVE_TAB_SERVICES)}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === ACTIVE_TAB_SERVICES
                ? 'border-green-500 text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Services
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-900/30 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-6 bg-green-900/30 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Tab Content */}
        {activeTab === ACTIVE_TAB_DATA_SOURCES && (
          <div className="space-y-8">
            {!selectedDataSource ? (
              /* Data Sources List */
              <div className="bg-[#222222] rounded-lg p-6">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-white">Connected Data Sources</h2>
                  <p className="text-sm text-gray-400 mt-1">Click on a data source to configure its settings</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allDataSources.map((source, index) => (
                    <button
                      key={index}
                      onClick={() => source.configurable && handleDataSourceClick(source.name)}
                      className={`border border-gray-600 rounded-lg p-4 flex items-center justify-between text-left transition-all ${
                        source.configurable
                          ? 'hover:border-green-500 hover:bg-[#2A2A2A] cursor-pointer'
                          : 'cursor-default'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <img src={`/assets/logos/${source.logo}`} alt={source.name} className="w-6 h-6 brightness-0 invert" />
                        <div>
                          <h3 className="font-semibold text-white">{source.name}</h3>
                          <p className="text-xs text-gray-400 mt-0.5">Click to view (read-only)</p>
                        </div>
                      </div>
                      <span className="text-xs bg-green-600 px-2 py-1 rounded">Connected</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Data Source Configuration Panel */
              <div className="bg-[#222222] rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleCancelEdit}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </button>
                    <div>
                      <h2 className="text-xl font-semibold text-white">{selectedDataSource} Configuration</h2>
                      <p className="text-sm text-gray-400 mt-1">
                        Configure {selectedDataSource} integration settings
                      </p>
                    </div>
                  </div>
                </div>

                {tenantConfigFormData && (
                  /* Configuration Form */
                  <div className="space-y-6">

                    {selectedDataSource === DATA_SOURCE_GRAFANA && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Grafana Connection</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Base URL</label>
                          <input
                            type="text"
                            value={tenantConfigFormData.grafana.base_url}
                            onChange={(e) => handleTenantConfigChange(['grafana', 'base_url'], e.target.value)}
                            readOnly
                            className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white cursor-not-allowed opacity-75"
                            placeholder="https://grafana.example.com"
                          />
                        </div>
                      </div>
                    )}

                    {selectedDataSource === DATA_SOURCE_GITHUB && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">GitHub Organization</h3>

                        {tenantConfigFormData.github.organization_url && (
                          <div className="bg-[#1A1A1A] border border-gray-600 rounded-lg p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div>
                                  <a
                                    href={tenantConfigFormData.github.organization_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-lg font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                                  >
                                    {tenantConfigFormData.github.organization_url}
                                  </a>
                                  <p className="text-xs text-gray-400 mt-1">Bazaar Technologies Organization</p>
                                </div>
                              </div>
                              <span className="text-xs bg-green-600 px-3 py-1 rounded">Connected</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedDataSource === DATA_SOURCE_GRAFANA_ONCALL && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Grafana OnCall Instance</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">OnCall URL</label>
                          <input
                            type="text"
                            value={tenantConfigFormData.grafana_oncall?.url || ''}
                            onChange={(e) => handleTenantConfigChange(['grafana_oncall', 'url'], e.target.value)}
                            readOnly
                            className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white cursor-not-allowed opacity-75"
                            placeholder="https://oncall.example.com"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Enter the URL of your Grafana OnCall instance
                          </p>
                        </div>
                        {tenantConfigFormData.grafana_oncall?.url && (
                          <div className="bg-[#1A1A1A] border border-gray-600 rounded-lg p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-lg font-semibold text-white">
                                    {tenantConfigFormData.grafana_oncall.url}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">Grafana OnCall Instance</p>
                                </div>
                              </div>
                              <span className="text-xs bg-green-600 px-3 py-1 rounded">Connected</span>
                            </div>
                          </div>
                        )}

                        {/* Filtering Rules Section */}
                        <div className="mt-6 pt-6 border-t border-gray-600">
                          <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2 mb-4">
                            Alert Filtering Rules
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                                Allowed Severities
                              </label>
                              <p className="text-xs text-gray-400 mb-3">
                                Only alerts with these severities will be processed. Others will be filtered out.
                              </p>
                              {tenantConfigFormData.grafana_oncall?.filtering_rules?.allowed_severities?.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {tenantConfigFormData.grafana_oncall.filtering_rules.allowed_severities.map((severity: string, index: number) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-600 text-white"
                                    >
                                      {severity}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div className="bg-[#1A1A1A] border border-gray-600 rounded-lg p-4">
                                  <p className="text-sm text-gray-400 italic">
                                    No filtering rules configured. All alerts will be processed.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedDataSource === DATA_SOURCE_NOTION && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Notion COE Database</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Database ID</label>
                          <input
                            type="text"
                            value={tenantConfigFormData.notion.database_id || ''}
                            onChange={(e) => handleTenantConfigChange(['notion', 'database_id'], e.target.value)}
                            readOnly
                            className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg px-3 py-2 text-white cursor-not-allowed opacity-75"
                            placeholder="12345678-1234-1234-1234-123456789abc"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Find this in your Notion database URL: https://www.notion.so/&#123;workspace_name&#125;/&#123;database_id&#125;
                          </p>
                        </div>
                        {tenantConfigFormData.notion.database_id && (
                          <div className="bg-[#1A1A1A] border border-gray-600 rounded-lg p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-1.055-.325-1.055-1.214V9.268l-1.4.14c-.514.047-.748-.14-.748-.653V7.92c0-.466.233-.747.748-.793l4.018-.233c.514-.047.748.14.748.607zm-1.68 1.68v9.055c0 .327-.14.514-.514.514h-.933c-.374 0-.514-.187-.514-.514V9.268c0-.327.14-.514.514-.514h.933c.374 0 .514.187.514.514z"/>
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-lg font-semibold text-white">
                                    {tenantConfigFormData.notion.database_id}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">COE (Correction of Errors) Database</p>
                                </div>
                              </div>
                              <span className="text-xs bg-green-600 px-3 py-1 rounded">Connected</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === ACTIVE_TAB_TEAMS && (
          <div className="space-y-8">
            {loading && !configData ? (
              <div className="text-center text-gray-400 py-12">Loading teams configuration...</div>
            ) : (
              <>
                {/* Add Team Form */}
                {showAddTeam && !editingTeam && (
                  <div className="mb-6">
                    {renderAddTeamForm()}
                  </div>
                )}

                {/* Edit Team Form */}
                {editingTeam && !showAddTeam && (
                  <div className="mb-6">
                    {renderTeamForm(editingTeamName, editingTeamSlackChannel)}
                  </div>
                )}

                {/* Teams List */}
                {!editingTeam && !showAddTeam && (
              <div className="bg-[#222222] rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white">Teams</h2>
                    <p className="text-sm text-gray-400 mt-1">
                      Manage teams and their associated Slack channels
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddTeam(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Team</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {configData?.team_configs.map((team) => {
                    const servicesInTeam = configData?.service_configs.filter(s => s.team_id === team.id) || [];
                    return (
                      <div
                        key={team.id}
                        className="border border-gray-600 rounded-lg p-4 hover:border-gray-500 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-white">{team.team_name}</h3>
                              <span className="text-xs bg-gray-700 px-2 py-1 rounded">
                                {servicesInTeam.length} service{servicesInTeam.length !== 1 ? 's' : ''}
                              </span>
                            </div>

                            <div className="space-y-2 text-sm">
                              {team.slack_channel && (
                                <div>
                                  <span className="text-gray-400">Slack Channel:</span>{' '}
                                  <span className="text-white font-mono">{team.slack_channel}</span>
                                </div>
                              )}

                              {servicesInTeam.length > 0 && (
                                <div>
                                  <span className="text-gray-400">Services:</span>
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    {servicesInTeam.map((service) => (
                                      <span
                                        key={service.id}
                                        className="inline-block bg-gray-700 text-white text-xs px-2 py-1 rounded"
                                      >
                                        {service.service_name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleEditTeam(team.id, team.team_name, team.slack_channel || '')}
                              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                              title="Edit Team"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {team.team_name !== 'Unassigned' && (
                              <button
                                onClick={() => handleDeleteTeam(team.id, team.team_name)}
                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                                title="Delete Team"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {configData && configData.team_configs.length === 0 && (
                    <div className="text-center text-gray-400 py-12">
                      No teams configured yet. Click "Add Team" to create one.
                    </div>
                  )}
                </div>
              </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === ACTIVE_TAB_SERVICES && (
          <div className="space-y-8">
            {loading && !configData ? (
              <div className="text-center text-gray-400 py-12">Loading services configuration...</div>
            ) : (
              <>
                {/* Show add service form */}
                {showAddService && (
                  <div className="mb-6">
                    {renderServiceForm('', editFormData || {
                      github: { repo: '', default_branch: 'main' },
                      grafana: {
                        dashboard_uid: '',
                        dashboard_name: '',
                        variables: { namespace: '', datasource: '' },
                        prometheus_datasource_uid: '',
                        loki_datasource_uid: ''
                      },
                      team_id: '',
                      description: ''
                    })}
                  </div>
                )}

                {/* Services List */}
                {!showAddService && !editingService && (
                  <div className="bg-[#222222] rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-semibold text-white">Services</h2>
                        <p className="text-sm text-gray-400 mt-1">
                          Configure service mappings for GitHub repos and Grafana dashboards
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setShowAddService(true);
                          setEditFormData({
                            github: { repo: '', default_branch: 'main' },
                            grafana: {
                              dashboard_uid: '',
                              dashboard_name: '',
                            variables: { namespace: '', datasource: '' },
                            prometheus_datasource_uid: '',
                            loki_datasource_uid: ''
                          },
                          team_id: '',
                          description: ''
                        });
                      }}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Service</span>
                      </button>
                    </div>

                    <div className="space-y-4">
                      {configData?.service_configs.map((serviceConfig) => (
                        <div
                          key={serviceConfig.service_name}
                          className="border border-gray-600 rounded-lg p-4 hover:border-gray-500 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-white mb-2">{serviceConfig.service_name}</h3>
                              <p className="text-sm text-gray-400 mb-3">{serviceConfig.description}</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div className="space-y-1">
                                  <div>
                                    <span className="text-gray-400">GitHub Repo:</span>{' '}
                                    <span className="text-white font-mono">{serviceConfig.github_repo}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Branch:</span>{' '}
                                    <span className="text-white">{serviceConfig.github_default_branch}</span>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <div>
                                    <span className="text-gray-400">Grafana Dashboard:</span>{' '}
                                    <span className="text-white">{serviceConfig.grafana_dashboard_name}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Team:</span>{' '}
                                    <span className="text-white">{configData?.team_configs.find(t => t.id === serviceConfig.team_id)?.team_name || 'Unknown'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => handleEditService(serviceConfig.id, serviceConfig.service_name, {
                                  github: { repo: serviceConfig.github_repo, default_branch: serviceConfig.github_default_branch },
                                  grafana: {
                                    dashboard_uid: serviceConfig.grafana_dashboard_uid,
                                    dashboard_name: serviceConfig.grafana_dashboard_name,
                                    variables: { namespace: serviceConfig.grafana_namespace },
                                    prometheus_datasource_uid: serviceConfig.grafana_prometheus_datasource_uid,
                                    loki_datasource_uid: serviceConfig.grafana_loki_datasource_uid
                                  },
                                  team_id: serviceConfig.team_id,
                                  description: serviceConfig.description
                                })}
                                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteService(serviceConfig.id, serviceConfig.service_name)}
                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {configData && configData.service_configs.length === 0 && (
                        <div className="text-center text-gray-400 py-12">
                          No services configured yet. Click "Add Service" to create one.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Edit Service Form */}
                {editingService && editFormData && !showAddService && (
                  <div>
                    {renderServiceForm(editingServiceName || '', editFormData)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Custom Delete Confirmation Modal */}
        {showDeleteConfirm && serviceToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#2A2A2A] rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Delete Service</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone.</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-300">
                  Are you sure you want to delete the service <span className="font-semibold text-white">'{serviceToDelete.name}'</span>?
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteService}
                  disabled={loading}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteService}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading && (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{loading ? 'Deleting...' : 'Delete'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Team Delete Confirmation Modal */}
        {showTeamDeleteConfirm && teamToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#2A2A2A] rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Delete Team</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone.</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-300">
                  Are you sure you want to delete the team <span className="font-semibold text-white">'{teamToDelete.name}'</span>?
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteTeam}
                  disabled={loading}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteTeam}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading && (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{loading ? 'Deleting...' : 'Delete'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
